import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { clientPortalApi } from '../api';
import { Avatar, Button, ThemeToggle, ToastContainer, Icon } from '@metrodev/core-web';

/** ISSUE-191: secciones del portal. `end` en Menús para que no quede activo en las demás rutas `/portal/*`. */
const PORTAL_TABS: { label: string; to: string; end?: boolean }[] = [
  { label: 'Menús', to: '/portal', end: true },
  { label: 'Comensales', to: '/portal/comensales' },
  { label: 'Reportes', to: '/portal/reportes' },
  { label: 'Comprobantes', to: '/portal/comprobantes' },
  { label: 'Reclamos', to: '/portal/reclamos' },
];

/**
 * ISSUE-143: shell propio del portal de cliente — deliberadamente NO
 * reutiliza `Layout` (sidebar interno, `NAV_ITEMS`, breadcrumbs de módulos
 * internos): un usuario Cliente (RR.HH.) no debe ver nada de eso, ni
 * siquiera oculto. Header oscuro fiel al diseño de la issue, con la misma
 * foto que el logo del cliente (asignada automáticamente — es el mismo
 * archivo, no una foto propia del usuario) y el mismo selector de tema que
 * usa el header interno.
 */
export default function ClientPortalLayout() {
  const { user, logout } = useAuth();

  const { data: clientInfo } = useQuery({
    queryKey: ['client-portal', 'client-info'],
    queryFn: () => clientPortalApi.getClientInfo(),
  });

  const { data: logoBlob } = useQuery({
    queryKey: ['client-portal', 'client-logo'],
    queryFn: () => clientPortalApi.getClientLogo(),
    enabled: !!clientInfo?.hasLogo,
    retry: false,
  });

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!logoBlob) {
      setLogoUrl(null);
      return;
    }
    const url = URL.createObjectURL(logoBlob);
    setLogoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoBlob]);

  const fullName = user ? `${user.firstName} ${user.lastName}` : '';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-app)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px clamp(16px, 4vw, 32px)',
          background: 'var(--color-sidebar)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: '#f5a623', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontFamily: 'var(--font-display)' }}>M</span>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--color-sidebar-text)' }}>
            Mirasoles · Portal de clientes
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Acceso directo al portal del comensal (login propio, código+PIN), para que RR.HH. lo comparta con su gente */}
          {clientInfo?.dinerChoiceEnabled && clientInfo.portalSlug && (
            <Button
              variant="secondary"
              onClick={() => window.open(`/${clientInfo.portalSlug}`, '_blank', 'noopener,noreferrer')}
            >
              <Icon name="external-link" size={14} />
              Portal del comensal
            </Button>
          )}

          <ThemeToggle />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 4px', borderRadius: 999, background: 'var(--color-sidebar-footer)' }}>
            {logoUrl ? (
              <img src={logoUrl} alt={clientInfo?.name ?? ''} style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <Avatar name={fullName} size={30} />
            )}
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-sidebar-text)', whiteSpace: 'nowrap' }}>
              {clientInfo?.name ?? '…'} · {fullName}, RR. HH.
            </span>
          </div>

          <button
            onClick={() => {
              logout();
              // #314: volver al login de SU empresa (con logo y nombre), no al login genérico.
              window.location.assign(clientInfo?.portalSlug ? `/${clientInfo.portalSlug}/rrhh` : '/login');
            }}
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-sidebar-text-muted)', display: 'flex', padding: 6, borderRadius: 6,
            }}
          >
            <Icon name="logout" size={18} />
          </button>
        </div>
      </header>

      <nav aria-label="Secciones del portal" style={{ display: 'flex', gap: 4, overflowX: 'auto', padding: '8px clamp(16px, 4vw, 32px) 0', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
        {PORTAL_TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            style={({ isActive }) => ({
              padding: '8px 14px',
              fontSize: 13.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              color: isActive ? 'var(--color-primary)' : 'var(--color-text-muted)',
              borderBottom: `2px solid ${isActive ? 'var(--color-primary)' : 'transparent'}`,
            })}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <main style={{ maxWidth: 900, margin: '0 auto' }}>
        <Outlet />
      </main>

      <ToastContainer />
    </div>
  );
}
